import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Search, CreditCard, CheckCircle, ArrowRight } from "lucide-react";

const InteractiveDemoSection = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 0,
      title: "Search for Parking",
      description: "Enter your destination and preferred time",
      icon: Search,
      image: "https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=400&h=300&fit=crop",
      details: "Simply enter where you need to park and when. Our smart search finds available spots near your destination."
    },
    {
      id: 1,
      title: "Choose Your Spot",
      description: "Browse available spots with photos and details",
      icon: MapPin,
      image: "https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=400&h=300&fit=crop",
      details: "View detailed information about each parking spot including photos, security features, and walking distance to your destination."
    },
    {
      id: 2,
      title: "Secure Payment",
      description: "Book instantly with secure payment",
      icon: CreditCard,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
      details: "Complete your booking with our secure payment system. Get instant confirmation and directions to your spot."
    },
    {
      id: 3,
      title: "Park with Confidence",
      description: "Arrive at your reserved spot worry-free",
      icon: CheckCircle,
      image: "https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=400&h=300&fit=crop",
      details: "Your parking spot is guaranteed and waiting for you. No more circling around looking for parking!"
    }
  ];

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % steps.length);
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">How Bayzo Works</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Booking parking has never been easier. See how you can reserve your spot in just a few clicks.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Step Navigation */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => goToStep(index)}
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                      index === currentStep
                        ? "bg-primary text-primary-foreground"
                        : index < currentStep
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-2 ${
                      index < currentStep ? "bg-primary" : "bg-muted"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Display */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                  {(() => {
                    const IconComponent = steps[currentStep].icon;
                    return <IconComponent className="h-6 w-6 text-primary" />;
                  })()}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{steps[currentStep].title}</h3>
                      <p className="text-muted-foreground">{steps[currentStep].description}</p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed mb-6">
                    {steps[currentStep].details}
                  </p>
                  <Button onClick={nextStep} className="group">
                    {currentStep === steps.length - 1 ? "Start Over" : "Next Step"}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
                <div className="order-first lg:order-last">
                  <img
                    src={steps[currentStep].image}
                    alt={steps[currentStep].title}
                    className="rounded-lg shadow-lg w-full h-64 object-cover"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {steps.map((step, index) => (
              <Card 
                key={step.id} 
                className={`cursor-pointer transition-all ${
                  index === currentStep ? "ring-2 ring-primary" : "hover:shadow-md"
                }`}
                onClick={() => goToStep(index)}
              >
                <CardContent className="p-4 text-center">
                  {(() => {
                    const IconComponent = step.icon;
                    return <IconComponent className={`h-8 w-8 mx-auto mb-2 ${
                      index === currentStep ? "text-primary" : "text-muted-foreground"
                    }`} />;
                  })()}
                  <h4 className="font-medium text-sm">{step.title}</h4>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemoSection;